import { useEffect, useState } from 'react';
import { localeUI } from '../lib/format';
import { Link } from 'react-router-dom';
import { Send, Timer, Users, Wrench, X } from 'lucide-react';
import { api } from '../lib/api';
import { useI18n } from '../lib/i18n';
import { usePersistentState } from '../lib/usePersistentState';
import { ClaimCocTab, CocStatusBadge } from './ClaimCocTab';
import { ClaimDrawer, ClientNameButton } from './ClaimDrawer';
import { InsuranceLink } from './InsuranceCard';
import { ViewSettingsButton, ViewSettingsModal } from './ViewSettingsModal';

/**
 * Cola Claims → COC. Dos secciones fijas: To Send (sin enviar) y Sent. Cada fila:
 * Homeowner · Claim # · Aseguradora · Equipo de Claims · Days in Queue · badges
 * COC/Photos (sin follow-up: acá se cuentan días, no vence nada). Al desplegar,
 * el mismo panel del tab COC (file, fotos, comparación IA, historial, envío).
 */

type Row = {
  claim_id: string;
  homeowner_name: string | null;
  claim_number: string | null;
  insurance_company: string | null;
  contractor_name: string | null;
  assigned_team_name: string | null;
  coc_status: string;
  photos_status: string;
  sent_at: string | null;
  sent_to: string | null;
  created_at: string;
};

const daysSince = (iso: string): number => Math.max(0, Math.floor((Date.now() - new Date(iso).getTime()) / 86400000));
const fmtWhen = (iso: string) => new Date(iso).toLocaleString(localeUI(), { dateStyle: 'medium', timeStyle: 'short' });

function CocRow({ r, onClient, onChanged }: { r: Row; onClient: (id: string) => void; onChanged: () => void }) {
  const { t } = useI18n();
  const [open, setOpen] = usePersistentState<boolean>(`open:coc:${r.claim_id}`, false);
  const days = daysSince(r.created_at);
  // "Sent outside the app": el COC ya salió por fuera (Gmail personal, etc.) → pide la fecha
  // real y aplica lo mismo que enviarlo desde aquí (Sent + Payments → Waiting Depreciation).
  const [askDate, setAskDate] = useState(false);
  const [when, setWhen] = useState(() => new Date().toISOString().slice(0, 10));
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  async function markOutside() {
    setBusy(true); setErr(null);
    try {
      await api.post(`/api/claims/${r.claim_id}/coc/mark-sent-outside`, { sentAt: when });
      setAskDate(false);
      onChanged();
    } catch (e) { setErr(e instanceof Error ? e.message : 'Error'); }
    finally { setBusy(false); }
  }
  return (
    <div className="card" style={{ margin: 0, padding: '10px 14px' }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap', cursor: 'pointer' }} onClick={() => setOpen((o) => !o)}>
        <span style={{ width: 14, textAlign: 'center' }}>{open ? '▾' : '▸'}</span>
        {/* Mismo estilo de nombre que el resto de los boards (tinta, no naranja). */}
        <span onClick={(e) => e.stopPropagation()}>
          <ClientNameButton claimId={r.claim_id} name={r.homeowner_name ?? '—'} onOpen={onClient} />
        </span>
        {r.claim_number && (
          <Link to={`/claims/${r.claim_id}`} onClick={(e) => e.stopPropagation()} className="muted" style={{ fontSize: 'var(--fs-sm)', fontFamily: 'var(--font-mono)' }}>
            #{r.claim_number}
          </Link>
        )}
        {r.insurance_company && (
          <span onClick={(e) => e.stopPropagation()}><InsuranceLink name={r.insurance_company} /></span>
        )}
        {/* Contratista visible SIN abrir el drawer (pedido del dueño). */}
        {r.contractor_name && <span className="badge" style={{ background: 'var(--surface-2)' }} title={r.contractor_name}><Wrench size={12} strokeWidth={1.75} /> {r.contractor_name}</span>}
        {r.assigned_team_name && <span className="badge teal"><Users size={12} strokeWidth={1.75} /> {r.assigned_team_name}</span>}
        <span style={{ flex: 1 }} />
        <span className={`badge ${days >= 7 ? 'red' : 'gray'}`} title={t('coc.daysInQueue', { n: String(days) })}>
          <Timer size={12} strokeWidth={1.75} /> {t('coc.daysInQueue', { n: String(days) })}
        </span>
        <CocStatusBadge kind="coc" status={r.coc_status} />
        <CocStatusBadge kind="photos" status={r.photos_status} />
        {r.sent_at && <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('coc.sentOn', { when: fmtWhen(r.sent_at) })}</span>}
        {!r.sent_at && !askDate && (
          <button className="btn ghost sm" onClick={(e) => { e.stopPropagation(); setAskDate(true); }}>
            <Send size={14} strokeWidth={1.75} /> {t('coc.sentOutside')}
          </button>
        )}
        {!r.sent_at && askDate && (
          <span style={{ display: 'inline-flex', gap: 6, alignItems: 'center' }} onClick={(e) => e.stopPropagation()}>
            <span className="muted" style={{ fontSize: 'var(--fs-2xs)' }}>{t('coc.sentOutsideWhen')}</span>
            <input type="date" className="input sm" style={{ width: 'auto' }} value={when}
              max={new Date().toISOString().slice(0, 10)} onChange={(e) => setWhen(e.target.value)} />
            <button className="btn sm" disabled={busy || !when} onClick={() => void markOutside()}>
              {busy ? '…' : t('coc.sentOutsideConfirm')}
            </button>
            <button className="btn ghost sm" disabled={busy} onClick={() => setAskDate(false)}><X size={14} strokeWidth={1.75} /></button>
            {err && <span style={{ color: 'var(--red)', fontSize: 'var(--fs-2xs)' }}>{err}</span>}
          </span>
        )}
      </div>
      {open && (
        <div style={{ marginTop: 12, borderTop: '1px solid var(--line)', paddingTop: 12 }}>
          <ClaimCocTab claimId={r.claim_id} onChanged={onChanged} />
        </div>
      )}
    </div>
  );
}

export function CocQueueSection() {
  const { t } = useI18n();
  const [rows, setRows] = useState<Row[]>([]);
  const [loading, setLoading] = useState(true);
  const [sel, setSel] = useState<string | null>(null);
  const [tick, setTick] = useState(0);
  const [settings, setSettings] = useState(false);

  useEffect(() => {
    let alive = true;
    api.get<{ rows: Row[] }>('/api/coc/queue')
      .then((r) => { if (alive) setRows(r.rows); })
      .catch(() => { if (alive) setRows([]); })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [tick]);

  if (loading) return <div className="page-empty">{t('common.loadingDots')}</div>;

  const toSend = rows.filter((r) => !r.sent_at);
  const sent = [...rows.filter((r) => r.sent_at)].sort((a, b) => new Date(b.sent_at!).getTime() - new Date(a.sent_at!).getTime());
  const onChanged = () => setTick((n) => n + 1);

  const section = (title: string, list: Row[], badgeCls: string) => (
    <div style={{ marginBottom: 22 }}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
        <h3 style={{ margin: 0, fontSize: 'var(--fs-base)' }}>{title}</h3>
        <span className={`badge ${badgeCls}`}>{list.length}</span>
      </div>
      {list.length === 0 ? (
        <div className="muted" style={{ fontSize: 'var(--fs-sm)' }}>—</div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
          {list.map((r) => <CocRow key={r.claim_id} r={r} onClient={setSel} onChanged={onChanged} />)}
        </div>
      )}
    </div>
  );

  return (
    <div>
      {/* Título + ⚙ de la vista (plantillas de email y días del follow-up de COC). */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 6 }}>
        <h2 className="card-title" style={{ margin: 0 }}>COC</h2>
        <span style={{ flex: 1 }} />
        <ViewSettingsButton onClick={() => setSettings(true)} />
      </div>
      {settings && <ViewSettingsModal deptKey="claims" viewKey="COC" label="COC" onClose={() => setSettings(false)} />}
      {rows.length === 0 ? (
        <div className="page-empty">{t('coc.queueEmpty')}</div>
      ) : (
        <>
          {section(t('coc.queueToSend'), toSend, 'amber')}
          {section(t('coc.queueSent'), sent, 'green')}
        </>
      )}
      {sel && <ClaimDrawer claimId={sel} onClose={() => setSel(null)} />}
    </div>
  );
}
